import { FETCH_MODES, FETCH_STATUSES, JURISDICTION_SOURCE_ROLES, LEGAL_SOURCE_TARGET_TYPES, SOURCE_DOC_ROLES, SOURCE_KINDS, } from '../data/legal-source-vocab.js';
import { ApplyDeferredError } from './framework-requirement-service.js';
export class LegalSourceValidationError extends Error {
    errors;
    constructor(errors) {
        super(`Legal source record invalid: ${errors.join('; ')}`);
        this.errors = errors;
        this.name = 'LegalSourceValidationError';
    }
}
export function isLegalSourceTarget(t) {
    return LEGAL_SOURCE_TARGET_TYPES.includes(t);
}
const JURISDICTION_RE = /^[A-Z]{2}(-[A-Za-z0-9]+)*$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_RE = /^\d{4}-\d{2}-\d{2}(T[0-9:.]+Z?)?$/;
const str = (max) => (v) => v === null || v === undefined ? null : typeof v !== 'string' ? 'must be text' : v.length > max ? `longer than ${max} characters` : null;
const oneOf = (list) => (v) => v === null || v === undefined ? null : typeof v === 'string' && list.includes(v) ? null : `must be one of ${list.join(', ')}`;
const url = (v) => {
    if (v === null || v === undefined)
        return null;
    if (typeof v !== 'string' || v.length > 2048)
        return 'must be a URL';
    try {
        const u = new URL(v);
        return u.protocol === 'https:' || u.protocol === 'http:' ? null : 'must be an http(s) URL';
    }
    catch {
        return 'must be a URL';
    }
};
const re = (r, what) => (v) => v === null || v === undefined ? null : typeof v === 'string' && r.test(v) ? null : `must be ${what}`;
const nonEmpty = (v) => (typeof v === 'string' && v.trim() ? null : 'must not be empty');
const all = (...fs) => (v) => fs.map((f) => f(v)).find((e) => e) ?? null;
/** `default_effective_rule_json`: {kind:'fixed', month, day} | {kind:'days_after_adjournment', days}. */
export function effectiveRuleErrors(raw) {
    if (raw === null || raw === undefined)
        return null;
    let v = raw;
    if (typeof raw === 'string') {
        try {
            v = JSON.parse(raw);
        }
        catch {
            return 'must be JSON';
        }
    }
    const o = v;
    if (!o || typeof o !== 'object')
        return 'must be an object';
    if (o.kind === 'fixed') {
        const m = Number(o.month), d = Number(o.day);
        return Number.isInteger(m) && m >= 1 && m <= 12 && Number.isInteger(d) && d >= 1 && d <= 31 ? null : 'fixed needs month 1-12 and day 1-31';
    }
    if (o.kind === 'days_after_adjournment') {
        const n = Number(o.days);
        return Number.isInteger(n) && n >= 0 && n <= 730 ? null : 'days_after_adjournment needs days 0-730';
    }
    return "kind must be 'fixed' or 'days_after_adjournment'";
}
export const LEGAL_SOURCE_TABLES = {
    source_document: {
        table: 'source_documents', idCol: 'doc_id', idPrefix: 'doc', provenanceCol: 'provenance_kind',
        naturalKey: ['source_kind', 'source_id', 'url'],
        fields: {
            source_kind: { required: true, check: oneOf(SOURCE_KINDS) },
            source_id: { required: true, check: all(nonEmpty, str(200)) },
            doc_role: { required: true, check: oneOf(SOURCE_DOC_ROLES) },
            url: { required: true, check: url },
            final_url: { check: url },
            content_sha256: { check: re(/^[0-9a-f]{64}$/, '64 lowercase hex characters') },
            last_checked_at: { check: re(ISO_RE, 'an ISO date or timestamp') },
            last_changed_at: { check: re(ISO_RE, 'an ISO date or timestamp') },
            fetch_mode: { check: oneOf(FETCH_MODES) },
            fetch_status: { check: oneOf(FETCH_STATUSES) },
            fetch_note: { check: str(1000) },
        },
    },
    jurisdiction_source: {
        table: 'jurisdiction_sources', idCol: 'registry_id', idPrefix: 'jsrc', provenanceCol: 'source_kind',
        naturalKey: ['jurisdiction', 'role', 'url'],
        fields: {
            jurisdiction: { required: true, check: re(JURISDICTION_RE, "a jurisdiction code such as 'US-CA'") },
            role: { required: true, check: oneOf(JURISDICTION_SOURCE_ROLES) },
            url: { required: true, check: url },
            fetch_mode: { check: oneOf(FETCH_MODES) },
            caveat: { check: str(1000) },
            verified_at: { check: re(ISO_RE, 'an ISO date or timestamp') },
        },
    },
    legislative_session: {
        table: 'legislative_sessions', idCol: 'session_id', idPrefix: 'lsess', provenanceCol: 'source_kind',
        naturalKey: ['jurisdiction', 'session_label'],
        fields: {
            jurisdiction: { required: true, check: re(JURISDICTION_RE, "a jurisdiction code such as 'US-ME'") },
            session_label: { required: true, check: all(nonEmpty, str(200)) },
            convenes_on: { check: re(DATE_RE, 'YYYY-MM-DD') },
            adjourns_on: { check: re(DATE_RE, 'YYYY-MM-DD') },
            default_effective_rule: { check: str(500) },
            default_effective_rule_json: { check: effectiveRuleErrors },
            source_url: { check: url },
            note: { check: str(1000) },
        },
        crossCheck: (s) => typeof s.convenes_on === 'string' && typeof s.adjourns_on === 'string' && s.convenes_on > s.adjourns_on
            ? ['adjourns_on: before convenes_on']
            : [],
    },
};
/** Validation errors for a proposal's state ([] when valid). `change_type` 'deprecate' needs only the id. */
export function legalSourceStateErrors(target, changeType, state, targetId) {
    const spec = LEGAL_SOURCE_TABLES[target];
    if (changeType === 'deprecate')
        return targetId ? [] : [`${spec.idCol}: required to retire a row`];
    const errors = [];
    for (const k of Object.keys(state)) {
        if (k === spec.idCol)
            continue;
        const f = spec.fields[k];
        if (!f) {
            errors.push(`${k}: not a ${spec.table} field`);
            continue;
        }
        const e = f.check?.(state[k]);
        if (e)
            errors.push(`${k}: ${e}`);
    }
    if (changeType === 'create') {
        for (const [k, f] of Object.entries(spec.fields)) {
            if (f.required && (state[k] === undefined || state[k] === null || state[k] === ''))
                errors.push(`${k}: required`);
        }
    }
    else if (changeType === 'update') {
        if (!targetId)
            errors.push(`${spec.idCol}: required to update a row`);
        for (const k of spec.naturalKey)
            if (k in state && (state[k] === null || state[k] === ''))
                errors.push(`${k}: cannot be cleared`);
    }
    else {
        errors.push(`change_type: ${changeType} is not supported`);
    }
    if (spec.crossCheck)
        errors.push(...spec.crossCheck(state));
    if (state[spec.idCol] !== undefined && (typeof state[spec.idCol] !== 'string' || !/^[a-z0-9][a-z0-9._:-]{2,120}$/.test(String(state[spec.idCol])))) {
        errors.push(`${spec.idCol}: lowercase letters, digits and . _ : - only`);
    }
    return errors;
}
function randomSuffix() {
    const bytes = new Uint8Array(6);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
/**
 * Apply an APPROVED legal-source proposal. Returns the row id. A document of a law that is not in the register yet
 * defers (the law may still be in the queue); everything else that fails validation throws.
 */
export async function applyLegalSourceChange(db, change, opts = {}) {
    if (!isLegalSourceTarget(change.target_type))
        throw new Error(`Not a legal source target: ${change.target_type}`);
    const spec = LEGAL_SOURCE_TABLES[change.target_type];
    let state;
    try {
        state = JSON.parse(change.proposed_state ?? '{}');
    }
    catch {
        throw new LegalSourceValidationError(['proposed_state: not valid JSON']);
    }
    const errors = legalSourceStateErrors(change.target_type, change.change_type, state, change.target_id);
    if (errors.length)
        throw new LegalSourceValidationError(errors);
    const now = opts.now ?? new Date().toISOString();
    const provenance = change.source_kind === 'agent' ? 'research_pipeline' : change.source_kind;
    const reviewer = change.reviewer ?? null;
    const reviewedAt = change.reviewed_at ?? now;
    if (change.change_type === 'deprecate') {
        const res = await db.execute(`UPDATE ${spec.table} SET retired_at = COALESCE(retired_at, ?), reviewer = ?, reviewed_at = ?, updated_at = ? WHERE ${spec.idCol} = ?`, [now, reviewer, reviewedAt, now, change.target_id]);
        if ((res.meta?.changes ?? 1) === 0)
            throw new LegalSourceValidationError([`${spec.idCol}: ${change.target_id} does not exist`]);
        return change.target_id;
    }
    // A document of a law names a law in the register (the law may still be in the queue: wait for it).
    const sourceKind = state.source_kind ?? (change.change_type === 'update' ? undefined : null);
    if (change.target_type === 'source_document' && sourceKind === 'legislation' && typeof state.source_id === 'string') {
        const law = await db.query(`SELECT legislation_id FROM legislation WHERE legislation_id = ?`, [state.source_id]);
        if (law.results.length === 0) {
            throw new ApplyDeferredError(`Document names law ${state.source_id}, which is not in the register yet. Approve that law first.`, { target_type: 'legislation', target_id: state.source_id });
        }
    }
    // A JSON field given as an object is stored as JSON text (review #463 L8), never as "[object Object]".
    if (state.default_effective_rule_json !== null && typeof state.default_effective_rule_json === 'object') {
        state.default_effective_rule_json = JSON.stringify(state.default_effective_rule_json);
    }
    const cols = Object.keys(state).filter((k) => k in spec.fields);
    const stampCols = [spec.provenanceCol, 'reviewer', 'reviewed_at', 'updated_at'];
    const stampVals = [provenance, reviewer, reviewedAt, now];
    let id = change.change_type === 'update' ? change.target_id : null;
    if (change.change_type === 'create') {
        // Idempotent on the natural key: a create whose key exists updates that row (and un-retires it).
        // d1-in-list: bounded — the natural key (two or three fixed columns)
        const where = spec.naturalKey.map((k) => `${k} = ?`).join(' AND ');
        const existing = await db.query(`SELECT ${spec.idCol} AS id FROM ${spec.table} WHERE ${where}`, spec.naturalKey.map((k) => state[k]));
        id = existing.results[0]?.id ?? null;
        if (!id) {
            const newId = typeof state[spec.idCol] === 'string' ? String(state[spec.idCol]) : `${spec.idPrefix}-${randomSuffix()}`;
            const insertCols = [spec.idCol, ...cols, ...stampCols, 'created_at'];
            await db.execute(
            // d1-in-list: bounded — one row's columns from the fixed field spec (under 20)
            `INSERT INTO ${spec.table} (${insertCols.join(', ')}) VALUES (${insertCols.map(() => '?').join(', ')})`, [newId, ...cols.map((c) => state[c] ?? null), ...stampVals, now]);
            return newId;
        }
    }
    const setCols = [...cols, ...stampCols, 'retired_at'];
    const res = await db.execute(
    // d1-in-list: bounded — SET list over the fixed field spec (under 20 columns)
    `UPDATE ${spec.table} SET ${setCols.map((c) => `${c} = ?`).join(', ')} WHERE ${spec.idCol} = ?`, [...cols.map((c) => state[c] ?? null), ...stampVals, null, id]);
    if ((res.meta?.changes ?? 1) === 0)
        throw new LegalSourceValidationError([`${spec.idCol}: ${id} does not exist`]);
    return id;
}
/** Read rows of one legal-source table with equality filters on known columns. Editors only (route guard). */
export async function listLegalSourceRows(db, target, filters, opts = {}) {
    const spec = LEGAL_SOURCE_TABLES[target];
    const clauses = [];
    const params = [];
    for (const [k, v] of Object.entries(filters)) {
        if (v === undefined || v === '')
            continue;
        if (!(k in spec.fields))
            continue;
        clauses.push(`${k} = ?`);
        params.push(v);
    }
    if (!opts.includeRetired)
        clauses.push('retired_at IS NULL');
    const where = clauses.length ? `WHERE ${clauses.join(' AND ')}` : '';
    const order = spec.naturalKey.join(', ');
    const limit = Math.min(Math.max(opts.limit ?? 500, 1), 2000);
    const res = await db.query(`SELECT * FROM ${spec.table} ${where} ORDER BY ${order} LIMIT ?`, [...params, limit]);
    return res.results;
}
export async function getLegalSourceRow(db, target, id) {
    const spec = LEGAL_SOURCE_TABLES[target];
    const res = await db.query(`SELECT * FROM ${spec.table} WHERE ${spec.idCol} = ?`, [id]);
    return res.results[0] ?? null;
}
//# sourceMappingURL=legal-source-registry.js.map